/**
 * Archive + article query composables.
 *
 * Single source of truth for "what archives exist for this locale" is the
 * `settings` collection's per-locale `archives` array. Article and archive
 * content lives in dedicated `articles` / `archives` collections.
 */
import type { ArchiveDeclaration, VocabEntry, Article as ContentArticle } from '~~/content.config'
import { persistentCache } from '~/utils/asyncDataCache'
import { queryPages } from '~/utils/contentQueries'
import { querySettings } from '~/utils/queries/settings'

// Re-export the schema-derived shapes under the historical names this module's
// consumers import. Single source of truth lives in `content.config.ts`.
export type ArchiveVocabEntry = VocabEntry
export type ArchiveDecl = ArchiveDeclaration

// `Article` rows carry a runtime `path` (Nuxt-Content auto-injects every page's
// path into the row) on top of the schema's frontmatter fields.
export type Article = ContentArticle & { path: string }

/** Reactive list of archive declarations for a given locale.
 *
 * Reactive on the passed Ref: switching locale at runtime (locale picker)
 * triggers a refetch of the new locale's `settings.md` via `useAsyncData`'s
 * `watch`, so consumers (the nav builder, the locale switcher) stay in
 * sync without having to remount.
 */
export function useArchives(locale: string | Ref<string>): ComputedRef<ArchiveDecl[]> {
  const { data } = useAsyncData(
    () => `archives-${toValue(locale)}`,
    () => querySettings(toValue(locale)),
    {
      watch: isRef(locale) ? [locale] : [],
      ...persistentCache,
    },
  )

  return computed<ArchiveDecl[]>(() => data.value?.archives ?? [])
}

/** Find the archive whose `path` matches the URL slug for the current locale. */
export function useArchiveByPath(
  locale: string | Ref<string>,
  slug: string | Ref<string>,
): ComputedRef<ArchiveDecl | null> {
  const archives = useArchives(locale)
  return computed(() => archives.value.find(a => a.path === toValue(slug)) ?? null)
}

/** Resolve an archive in the target locale by translationKey (used by locale switcher). */
export async function findArchiveInLocale(
  translationKey: string,
  targetLocale: string,
): Promise<ArchiveDecl | null> {
  const settings = await querySettings(targetLocale)
  const archives = settings?.archives ?? []
  return archives.find(a => a.translationKey === translationKey) ?? null
}

interface ArticleQueryOptions {
  category?: string
  tag?: string
  /** Free-text query — case-insensitive substring match against title + description. */
  q?: string
  page?: number
  pageSize?: number
}

/** Paginated, optionally filtered article list for one archive. */
export function useArticles(
  archive: Ref<ArchiveDecl | null>,
  locale: Ref<string>,
  options: Ref<ArticleQueryOptions>,
) {
  const key = computed(() => {
    const a = archive.value
    if (!a) return 'articles-empty'
    const o = options.value
    const q = (o.q ?? '').trim().toLowerCase()
    return `articles-${locale.value}-${a.path}-${o.category ?? '*'}-${o.tag ?? '*'}-${q || '*'}-${o.page ?? 1}`
  })

  return useAsyncData(
    () => key.value,
    async () => {
      const a = archive.value
      if (!a) return { items: [] as Article[], total: 0, page: 1, pageSize: 12, totalPages: 0 }
      const o = options.value
      const pageSize = o.pageSize ?? a.pageSize
      const page = Math.max(1, o.page ?? 1)
      const prefix = `/${locale.value}/${a.path}/`

      let q = (await queryPages('articles'))
        .where('path', 'LIKE', `${prefix}%`)
        .order('date', 'DESC')
        // Project only what the card list + filter/search/locale-switch read —
        // omit `body` (the parsed MDC AST, by far the heaviest column), which
        // was loaded for every article in the archive then sliced to one page
        // and serialized into __NUXT_DATA__.
        .select('path', 'title', 'description', 'date', 'category', 'tags', 'featuredImage', 'translationKey', 'listed')

      if (o.category) q = q.where('category', '=', o.category)

      const all: Article[] = await q.all()
      // `listed: false` hides the article from every listing surface while
      // leaving its direct URL reachable. Filtered in-memory (not via `.where`)
      // because a missing/NULL `listed` must count as listed, and SQL
      // `listed <> false` drops NULLs.
      const visible = all.filter(a => a.listed !== false)
      // Tag filter is in-memory because it's an array column. Free-text search
      // is also in-memory — typing should narrow already-loaded results, not
      // round-trip the database. Title + description are the substring corpus.
      let filtered = o.tag ? visible.filter(a => a.tags?.includes(o.tag!)) : visible
      const needle = (o.q ?? '').trim().toLowerCase()
      if (needle) {
        filtered = filtered.filter(item => {
          const hay = `${item.title ?? ''} ${item.description ?? ''}`.toLowerCase()
          return hay.includes(needle)
        })
      }

      const total = filtered.length
      const totalPages = Math.max(1, Math.ceil(total / pageSize))
      const start = (page - 1) * pageSize
      return {
        items: filtered.slice(start, start + pageSize),
        total,
        page,
        pageSize,
        totalPages,
      }
    },
    { ...persistentCache },
  )
}

/** Single article by path. */
export function useArticle(path: Ref<string>) {
  return useAsyncData(
    () => `article-${path.value}`,
    async () => (await queryPages('articles')).path(path.value).first(),
    { ...persistentCache },
  )
}

/** Same-archive, same-category articles, excluding the current one, ordered by date. */
export function useRelatedArticles(article: Ref<Article | null>, limit = 3) {
  return useAsyncData(
    () => `related-${article.value?.path ?? 'none'}`,
    async (): Promise<Article[]> => {
      const a = article.value
      if (!a) return []
      const segments = a.path.split('/')
      // /{locale}/{archive}/{slug} → prefix is /{locale}/{archive}/
      const prefix = segments.slice(0, 3).join('/') + '/'
      // Fetch all same-category peers, then drop unlisted ones and slice to the
      // limit in-memory. Filtering after `.limit(limit)` at the DB level would
      // let an unlisted article consume a slot; and `listed <> false` in SQL
      // would wrongly drop articles with no `listed` key (NULL).
      const items: Article[] = await (await queryPages('articles'))
        .where('path', 'LIKE', `${prefix}%`)
        .where('category', '=', a.category)
        .where('path', '<>', a.path)
        .order('date', 'DESC')
        .all()
      return items.filter(item => item.listed !== false).slice(0, limit)
    },
    { ...persistentCache },
  )
}

/** Resolve the article in the target locale that shares this article's translationKey. */
export async function resolveArticleLocaleSwitch(
  currentArticle: Article,
  targetLocale: string,
): Promise<string | null> {
  // Ask the DB for the one peer in the target locale (path only) instead of
  // loading every locale variant's full row and filtering in memory.
  const match = await (await queryPages('articles'))
    .where('translationKey', '=', currentArticle.translationKey)
    .where('path', 'LIKE', `/${targetLocale}/%`)
    .select('path')
    .first() as { path?: string } | null
  return match?.path ?? null
}
